import React, { useContext, useEffect, useReducer, useState, useRef } from "react";
import { Link as RouterLink, useHistory, useLocation } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import {
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Badge,
  Typography,
  useTheme,
} from "@material-ui/core";

// Ícones modernos de contorno (outline) do Material UI
import DashboardOutlinedIcon from "@material-ui/icons/DashboardOutlined";
import ChatOutlinedIcon from "@material-ui/icons/ChatOutlined";
import SettingsOutlinedIcon from "@material-ui/icons/SettingsOutlined";
import PermContactCalendarOutlinedIcon from "@material-ui/icons/PermContactCalendarOutlined";
import FlashOnOutlinedIcon from "@material-ui/icons/FlashOnOutlined";
import CodeOutlinedIcon from "@material-ui/icons/CodeOutlined";
import EventOutlinedIcon from "@material-ui/icons/EventOutlined";
import LabelOutlinedIcon from "@material-ui/icons/LabelOutlined";
import EventAvailableOutlinedIcon from "@material-ui/icons/EventAvailableOutlined";
import AnnouncementOutlinedIcon from "@material-ui/icons/AnnouncementOutlined";
import ForumOutlinedIcon from "@material-ui/icons/ForumOutlined";
import TuneOutlinedIcon from "@material-ui/icons/TuneOutlined";
import AttachMoneyOutlinedIcon from '@material-ui/icons/AttachMoneyOutlined';
import ViewComfyOutlinedIcon from '@material-ui/icons/ViewComfyOutlined';
import CreateOutlinedIcon from '@material-ui/icons/CreateOutlined';
import ChatBubbleOutlineOutlinedIcon from '@material-ui/icons/ChatBubbleOutlineOutlined';
import AllInclusiveOutlinedIcon from '@material-ui/icons/AllInclusiveOutlined';
import AttachFileOutlinedIcon from '@material-ui/icons/AttachFileOutlined';
import TimelineOutlinedIcon from "@material-ui/icons/TimelineOutlined";

import { i18n } from "../translate/i18n";
import { WhatsAppsContext } from "../context/WhatsApp/WhatsAppsContext";
import { AuthContext } from "../context/Auth/AuthContext";
import { Can } from "../components/Can";
import { SocketContext } from "../context/Socket/SocketContext";
import { isArray } from "lodash";
import api from "../services/api";
import toastError from "../errors/toastError";
import usePlans from "../hooks/usePlans";
import { hasConnectionIssue } from "../utils/connectionStatus";
import { MANAGEMENT_PATHS } from "../utils/managementSections";

// Cores do tema principal do software (manter consistente)
const mainThemeColor = {
  light: '#5d8aff',
  main: '#2563eb',
  dark: '#1a44a8',
  contrastText: '#ffffff'
};

const secondaryThemeColor = {
  light: '#ff6e59',
  main: '#ff3f20',
  dark: '#c52100',
  contrastText: '#ffffff'
};

const useStyles = makeStyles((theme) => ({
  listItem: {
    // The icon stays put when the floating menu expands: only the name appears beside it.
    height: 36,
    padding: "0 11px",
    borderRadius: 12,
    margin: "2px 6px",
    width: "calc(100% - 12px)",
    justifyContent: "flex-start",
    "&:hover": {
      backgroundColor: theme.palette.type === 'dark'
        ? 'rgba(255, 255, 255, 0.08)'
        : 'rgba(0, 0, 0, 0.04)',
    },
    "html[data-phone-navigation] &, html[data-tablet-navigation] &": {
      height: 48,
      minHeight: 48,
      width: "calc(100% - 16px)",
      padding: theme.spacing(0, 1.5),
      margin: theme.spacing(0.25, 1),
      borderRadius: 14,
      justifyContent: "flex-start",
      backgroundColor: "transparent",
      "& $listItemIcon": {
        color: theme.palette.text.secondary,
      },
      "& $listItemText": {
        color: theme.palette.text.primary,
      },
      "&:hover": {
        backgroundColor: theme.palette.type === "dark"
          ? "rgba(255, 255, 255, 0.05)"
          : "rgba(0, 0, 0, 0.04)",
      },
      // On touch the browser keeps :hover on the last tapped item.
      "@media (hover: none)": {
        "&:hover": { backgroundColor: "transparent" },
      },
      '&[aria-current="page"], &[aria-current="page"]:hover': {
        backgroundColor: "rgba(20, 164, 92, 0.12)",
        "& $listItemIcon": {
          color: theme.palette.type === "dark" ? theme.palette.primary.main : theme.palette.primary.dark,
        },
        "& $listItemText": {
          color: theme.palette.type === "dark" ? theme.palette.primary.main : theme.palette.primary.dark,
          fontWeight: 600,
        },
      },
      "&.Mui-focusVisible": {
        outline: `2px solid ${theme.palette.primary.main}`,
        outlineOffset: 2,
      },
    },
    // 44px keeps the phone's longer list on one screen.
    "html[data-tablet-navigation] &, html[data-phone-navigation] &": {
      height: 44,
      minHeight: 44,
      margin: theme.spacing(0, 1),
    },
  },
  menuGroupGap: {
    height: theme.spacing(1.5),
    "html[data-tablet-navigation] &": { height: theme.spacing(1) },
  },
  listItemActive: {
    backgroundColor: 'rgba(20, 164, 92, 0.12)',
    "&:hover": { backgroundColor: 'rgba(20, 164, 92, 0.18)' },
    "& $listItemIcon": {
      color: theme.palette.primary.main,
    },
    "& $listItemText": {
      "& span": {
        color: theme.palette.primary.main,
        fontWeight: 600,
      }
    },
  },
  listItemIcon: {
    minWidth: 22,
    width: 22,
    flexShrink: 0,
    marginRight: theme.spacing(1.5),
    justifyContent: "center",
    color: theme.palette.type === 'dark'
      ? theme.palette.grey[400]
      : theme.palette.text.secondary,
    "& svg": {
      fontSize: 22,
    },
    "html[data-phone-navigation] &, html[data-tablet-navigation] &": {
      minWidth: 22,
      width: 22,
      marginRight: theme.spacing(1.5),
      justifyContent: "center",
      flexShrink: 0,
      "& svg": {
        fontSize: 22,
      },
    },
  },
  listItemText: {
    fontSize: 14,
    fontWeight: 500,
    margin: 0,
    "& span": {
      fontSize: 14,
      color: theme.palette.type === 'dark'
        ? theme.palette.grey[200]
        : theme.palette.text.primary,
    },
    "html[data-phone-navigation] &, html[data-tablet-navigation] &": {
      fontSize: 16,
      lineHeight: "22px",
      fontWeight: 500,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
  },
  divider: {
    margin: theme.spacing(1, 0),
    backgroundColor: theme.palette.type === 'dark'
      ? 'rgba(255, 255, 255, 0.12)'
      : 'rgba(0, 0, 0, 0.06)',
  },
  nestedList: {
    paddingLeft: theme.spacing(2.5),
  },
  badge: {
    marginRight: theme.spacing(1),
    "& .MuiBadge-badge": {
      backgroundColor: theme.palette.type === 'dark'
        ? theme.palette.error.main
        : theme.palette.secondary.main,
    },
    "html[data-phone-navigation] &, html[data-tablet-navigation] &": {
      marginRight: 0,
    },
  },
  versionInfo: {
    fontSize: 11,
    padding: theme.spacing(1, 2),
    textAlign: "right",
    fontWeight: 500,
    color: theme.palette.type === 'dark'
      ? theme.palette.grey[500]
      : theme.palette.text.secondary,
  },
  connectionWarning: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
    height: 16,
    width: 16,
    fontSize: 10,
    marginLeft: 8,
  },
  expandIcon: {
    color: theme.palette.type === 'dark'
      ? theme.palette.grey[400]
      : theme.palette.text.secondary,
  }
}));

function ListItemLink(props) {
  const { icon, primary, to, className, badge, badgeColor, location, collapsed, activePaths } = props;
  const classes = useStyles({ collapsed });
  const { pathname } = useLocation();
  const theme = useTheme();
  const isActive = pathname === to;
  const isSectionActive = isActive
    || ((to === "/tickets" || to === "/chats") && pathname.startsWith(`${to}/`))
    || Boolean(activePaths && activePaths.includes(pathname));

  const renderLink = React.useMemo(
    () =>
      React.forwardRef((itemProps, ref) => (
        <RouterLink to={to} ref={ref} {...itemProps} />
      )),
    [to]
  );

  const listItemContent = (
    <ListItem
      button
      dense
      component={renderLink}
      aria-current={isSectionActive ? "page" : undefined}
      aria-label={collapsed ? primary : undefined}
      className={`${classes.listItem} ${isSectionActive ? classes.listItemActive : ''} ${className || ''}`}
    >
      {icon ? (
        <ListItemIcon className={classes.listItemIcon}>
          {badge ? (
            <Badge
              color={badgeColor || "secondary"}
              variant="dot"
              invisible={!badge}
              className={classes.badge}
            >
              {icon}
            </Badge>
          ) : (
            icon
          )}
        </ListItemIcon>
      ) : null}
      {!collapsed && (
        <ListItemText
          primary={primary}
          primaryTypographyProps={{
            className: classes.listItemText
          }}
        />
      )}
    </ListItem>
  );

  return (
    <li>
      {listItemContent}
    </li>
  );
}

const reducer = (state, action) => {
  if (action.type === "LOAD_CHATS") {
    const chats = action.payload;
    const newChats = [];

    if (isArray(chats)) {
      chats.forEach((chat) => {
        const chatIndex = state.findIndex((u) => u.id === chat.id);
        if (chatIndex !== -1) {
          state[chatIndex] = chat;
        } else {
          newChats.push(chat);
        }
      });
    }

    return [...state, ...newChats];
  }

  if (action.type === "UPDATE_CHATS") {
    const chat = action.payload;
    const chatIndex = state.findIndex((u) => u.id === chat.id);

    if (chatIndex !== -1) {
      state[chatIndex] = chat;
      return [...state];
    } else {
      return [chat, ...state];
    }
  }

  if (action.type === "DELETE_CHAT") {
    const chatId = action.payload;

    const chatIndex = state.findIndex((u) => u.id === chatId);
    if (chatIndex !== -1) {
      state.splice(chatIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }

  if (action.type === "CHANGE_CHAT") {
    const changedChats = state.map((chat) => {
      if (chat.id === action.payload.chat.id) {
        return action.payload.chat;
      }
      return chat;
    });
    return changedChats;
  }
};

const MainListItems = (props) => {
  const { drawerClose, collapsed, onExpandDrawer, isMobilePwaMode = false } = props;
  const classes = useStyles({ collapsed });
  const theme = useTheme();
  const { whatsApps } = useContext(WhatsAppsContext);
  const { user, isCompanyRestricted: companyRestricted, billingLoading } = useContext(AuthContext);
  const isCompanyRestricted = companyRestricted || billingLoading;
  const [connectionWarning, setConnectionWarning] = useState(false);
  const [showCampaigns, setShowCampaigns] = useState(false);
  const [showAutomation, setShowAutomation] = useState(false);
  const [showKanban, setShowKanban] = useState(false);
  const [showOpenAi, setShowOpenAi] = useState(false);
  const [showSchedules, setShowSchedules] = useState(false);
  const [showInternalChat, setShowInternalChat] = useState(false);
  const [showExternalApi, setShowExternalApi] = useState(false);
  const history = useHistory();

  const [invisible, setInvisible] = useState(true);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchParam] = useState("");
  const [chats, dispatch] = useReducer(reducer, []);
  const { getPlanCompany } = usePlans();
  const isMountedRef = useRef(true);

  const socketManager = useContext(SocketContext);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    if (!user?.companyId || isCompanyRestricted) {
      return;
    }

    async function fetchData() {
      const companyId = user.companyId;
      try {
        const planConfigs = await getPlanCompany(undefined, companyId);
        if (!isMountedRef.current) return;

        setShowCampaigns(planConfigs.plan.useCampaigns);
        setShowAutomation(planConfigs.plan.useAutomation);
        setShowKanban(planConfigs.plan.useKanban);
        setShowOpenAi(planConfigs.plan.useOpenAi);
        setShowSchedules(planConfigs.plan.useSchedules);
        setShowInternalChat(planConfigs.plan.useInternalChat);
        setShowExternalApi(planConfigs.plan.useExternalApi);
      } catch (err) {
        if (!isMountedRef.current) return;
        toastError(err);
      }
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.companyId, isCompanyRestricted]);

  useEffect(() => {
    if (isCompanyRestricted) return undefined;
    const delayDebounceFn = setTimeout(() => {
      fetchChats();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParam, pageNumber, isCompanyRestricted]);

  useEffect(() => {
    if (isCompanyRestricted) return undefined;

    const companyId = localStorage.getItem("companyId");
    if (!companyId) return undefined;
    const socket = socketManager.getSocket(companyId);

    socket.on(`company-${companyId}-chat`, (data) => {
      if (data.action === "new-message") {
        dispatch({ type: "CHANGE_CHAT", payload: data });
      }
      if (data.action === "update") {
        dispatch({ type: "CHANGE_CHAT", payload: data });
      }
    });
    return () => {
      socket.disconnect();
    };
  }, [socketManager, isCompanyRestricted]);

  useEffect(() => {
    let unreadsCount = 0;
    if (isCompanyRestricted) {
      setInvisible(true);
      return;
    }
    if (chats.length > 0) {
      for (let chat of chats) {
        for (let chatUser of chat.users) {
          if (chatUser.userId === user.id) {
            unreadsCount += chatUser.unreads;
          }
        }
      }
    }
    if (unreadsCount > 0) {
      setInvisible(false);
    } else {
      setInvisible(true);
    }
  }, [chats, user?.id, isCompanyRestricted]);

  useEffect(() => {
    if (isCompanyRestricted) return undefined;

    const delayDebounceFn = setTimeout(() => {
      if (whatsApps.length > 0) {
        if (isMountedRef.current) setConnectionWarning(hasConnectionIssue(whatsApps));
      }
    }, 2000);
    return () => clearTimeout(delayDebounceFn);
  }, [whatsApps, isCompanyRestricted]);

  const fetchChats = async () => {
    if (isCompanyRestricted) return;
    try {
      const { data } = await api.get("/chats/", {
        params: { searchParam, pageNumber },
      });
      if (!isMountedRef.current) return;
      dispatch({ type: "LOAD_CHATS", payload: data.records });
    } catch (err) {
      if (!isMountedRef.current) return;
      toastError(err);
    }
  };

  // Função para fechar submenus (mantida para compatibilidade com window.closeSubmenus)
  // eslint-disable-next-line no-unused-vars
  const closeSubmenus = () => {
    // submenus foram removidos — sem ação necessária
  };

  // Registrar função no window object para acesso do componente pai
  useEffect(() => {
    window.closeSubmenus = closeSubmenus;
    return () => {
      delete window.closeSubmenus;
    };
  }, []);

  // Fechar submenus quando drawer é recolhido
  useEffect(() => {
    if (collapsed) {
      closeSubmenus();
    }
  }, [collapsed]);

  if (isCompanyRestricted) {
    return (
      <div onClick={drawerClose}>
        <ListItemLink
          to="/financeiro"
          primary={i18n.t("mainDrawer.listItems.financeiro")}
          icon={<AttachMoneyOutlinedIcon />}
          collapsed={collapsed}
        />
      </div>
    );
  }

  if (isMobilePwaMode) {
    return (
      <div onClick={drawerClose}>
        <Can
          role={user.profile}
          perform="dashboard:view"
          yes={() => (
            <ListItemLink
              to="/"
              primary="Dashboard"
              icon={<DashboardOutlinedIcon />}
              collapsed={collapsed}
            />
          )}
        />

        <ListItemLink
          to="/tickets"
          primary={i18n.t("mainDrawer.listItems.tickets")}
          icon={<ChatBubbleOutlineOutlinedIcon />}
          collapsed={collapsed}
        />

        {showKanban && (
          <ListItemLink
            to="/kanban"
            primary="Kanban"
            icon={<ViewComfyOutlinedIcon />}
            collapsed={collapsed}
          />
        )}

        <ListItemLink
          to="/quick-messages"
          primary={i18n.t("mainDrawer.listItems.quickMessages")}
          icon={<FlashOnOutlinedIcon />}
          collapsed={collapsed}
        />

        <ListItemLink
          to="/contacts"
          primary={i18n.t("mainDrawer.listItems.contacts")}
          icon={<PermContactCalendarOutlinedIcon />}
          collapsed={collapsed}
        />

        <ListItemLink
          to="/schedules"
          primary={i18n.t("mainDrawer.listItems.schedules")}
          icon={<EventOutlinedIcon />}
          collapsed={collapsed}
        />

        <ListItemLink
          to="/tags"
          primary={i18n.t("mainDrawer.listItems.tags")}
          icon={<LabelOutlinedIcon />}
          collapsed={collapsed}
        />

        <ListItemLink
          to="/chats"
          primary={i18n.t("mainDrawer.listItems.chats")}
          icon={<ForumOutlinedIcon />}
          badge={!invisible}
          collapsed={collapsed}
        />

        <Can
          role={user.profile}
          perform="drawer-admin-items:view"
          yes={() => (
            <ListItemLink
              to="/connections"
              activePaths={MANAGEMENT_PATHS}
              primary={i18n.t("mainDrawer.listItems.management")}
              icon={<TuneOutlinedIcon />}
              badge={connectionWarning}
              badgeColor="error"
              collapsed={collapsed}
            />
          )}
        />

        <ListItemLink
          to="/financeiro"
          primary={i18n.t("mainDrawer.listItems.financeiro")}
          icon={<AttachMoneyOutlinedIcon />}
          collapsed={collapsed}
        />
      </div>
    );
  }

  return (
    <div onClick={drawerClose}>
      <Can
        role={user.profile}
        perform="dashboard:view"
        yes={() => (
          <ListItemLink
            to="/"
            primary="Dashboard"
            icon={<DashboardOutlinedIcon />}
            collapsed={collapsed}
          />
        )}
      />

      <ListItemLink
        to="/tickets"
        primary={i18n.t("mainDrawer.listItems.tickets")}
        icon={<ChatBubbleOutlineOutlinedIcon />}
        collapsed={collapsed}
      />

      {showKanban && (
        <ListItemLink
          to="/kanban"
          primary={`Kanban`}
          icon={<ViewComfyOutlinedIcon />}
          collapsed={collapsed}
        />
      )}

      <ListItemLink
        to="/quick-messages"
        primary={i18n.t("mainDrawer.listItems.quickMessages")}
        icon={<FlashOnOutlinedIcon />}
        collapsed={collapsed}
      />

      <ListItemLink
        to="/contacts"
        primary={i18n.t("mainDrawer.listItems.contacts")}
        icon={<PermContactCalendarOutlinedIcon />}
        collapsed={collapsed}
      />

      <ListItemLink
        to="/schedules"
        primary={i18n.t("mainDrawer.listItems.schedules")}
        icon={<EventOutlinedIcon />}
        collapsed={collapsed}
      />

      <ListItemLink
        to="/tags"
        primary={i18n.t("mainDrawer.listItems.tags")}
        icon={<LabelOutlinedIcon />}
        collapsed={collapsed}
      />

      <ListItemLink
        to="/chats"
        primary={i18n.t("mainDrawer.listItems.chats")}
        icon={<ForumOutlinedIcon />}
        badge={!invisible}
        collapsed={collapsed}
      />

      {showCampaigns && (
        <>
          <div className={classes.menuGroupGap} aria-hidden="true" />
          <ListItemLink
            to="/campaigns"
            primary={i18n.t("mainDrawer.listItems.campaigns")}
            icon={<EventAvailableOutlinedIcon />}
            collapsed={collapsed}
          />
        </>
      )}

      <Can
        role={user.profile}
        perform="drawer-admin-items:view"
        yes={() => (
          <>
            {!showCampaigns && (showAutomation || showOpenAi) && <div className={classes.menuGroupGap} aria-hidden="true" />}
            {showAutomation && (
              <ListItemLink
                to="/flowbuilders"
                primary={i18n.t("mainDrawer.listItems.flows")}
                icon={<TimelineOutlinedIcon />}
                collapsed={collapsed}
              />
            )}

            {showOpenAi && (
              <ListItemLink
                to="/prompts"
                primary={i18n.t("mainDrawer.listItems.prompts")}
                icon={<AllInclusiveOutlinedIcon />}
                collapsed={collapsed}
              />
            )}

            <div className={classes.menuGroupGap} aria-hidden="true" />
            <ListItemLink
              to="/connections"
              activePaths={MANAGEMENT_PATHS}
              primary={i18n.t("mainDrawer.listItems.management")}
              icon={<TuneOutlinedIcon />}
              badge={connectionWarning}
              badgeColor="error"
              collapsed={collapsed}
            />

            {showExternalApi && (
              <ListItemLink
                to="/messages-api"
                primary={i18n.t("mainDrawer.listItems.messagesAPI")}
                icon={<CodeOutlinedIcon />}
                collapsed={collapsed}
              />
            )}

            <ListItemLink
              to="/financeiro"
              primary={i18n.t("mainDrawer.listItems.financeiro")}
              icon={<AttachMoneyOutlinedIcon />}
              collapsed={collapsed}
            />

            <ListItemLink
              to="/settings"
              primary={i18n.t("mainDrawer.listItems.settings")}
              icon={<SettingsOutlinedIcon />}
              collapsed={collapsed}
            />
          </>
        )}
      />
    </div>
  );
};

export default MainListItems;
